import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ChevronDown, ChevronRight, CalendarDays, Truck, CheckCircle2, Hourglass, Camera, FileText } from "lucide-react";
import { toast } from "sonner";
import { Q, Pill, fechaLarga, fotoUrl } from "./util";

const hora = (iso) => (iso ? new Date(iso).toLocaleTimeString("es-GT", { timeZone: "America/Guatemala", hour: "2-digit", minute: "2-digit" }) : "—");

const Fila = ({ r, i, onChanged }) => {
  const conf = r.pagos.every((p) => p.estado_cuadre === "cuadrado");
  const confirmar = async () => {
    try {
      for (const p of r.pagos.filter((x) => x.estado_cuadre !== "cuadrado")) {
        await api.post("/domicilios/cuadre/pago", { order_id: r.id, pago_id: p.id, estado_cuadre: "cuadrado" });
      }
      toast.success(`Pago de ${r.numero} confirmado`); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const foto = r.pagos.find((p) => p.foto)?.foto;
  return (
    <tr className="border-t" data-testid={`ciudad-fila-${r.numero}`}>
      <td className="px-2 py-2 font-bold">{i + 1}</td>
      <td className="px-2 py-2"><span className="font-semibold">{r.cliente}</span> <span className="ml-1 rounded bg-violet-600 text-white text-[10px] font-bold px-1.5 py-0.5">{r.numero}</span></td>
      <td className="px-2 py-2 whitespace-nowrap"><Truck className="inline w-4 h-4 text-emerald-600 mr-1" />{hora(r.hora_entrega)}</td>
      <td className="px-2 py-2 text-xs">{r.productos}</td>
      <td className="px-2 py-2 text-xs">
        <div className="text-[11px] text-zinc-600" data-testid={`ciudad-anticipo-${r.numero}`}>
          Total <Q v={r.total} /> · Anticipo <b><Q v={r.anticipo.monto} /></b>{r.anticipo.monto > 0 && ` (${r.anticipo.banco || r.anticipo.metodo})`}
        </div>
        <div className="font-bold" data-testid={`ciudad-debe-${r.numero}`}>Debe cobrar: <Q v={r.debe_cobrar} /></div>
        {r.pagos.map((p) => <div key={p.id} className="flex justify-between gap-2"><span>{p.caja}</span><Q v={p.monto} /></div>)}
        {r.exceso > 0 && <div className="text-red-600 font-bold text-[11px]" data-testid={`ciudad-exceso-${r.numero}`}>Cobró de más: <Q v={r.exceso} /></div>}
      </td>
      <td className="px-2 py-2">{foto ? <a href={fotoUrl(foto)} target="_blank" rel="noreferrer"><img src={fotoUrl(foto)} alt="" className="w-12 h-9 object-cover rounded border" /></a> : <Camera className="w-5 h-5 text-red-500" />}</td>
      <td className="px-2 py-2">
        {conf ? <Pill tone="green"><CheckCircle2 className="w-3 h-3" />Confirmado</Pill>
          : <button onClick={confirmar} data-testid={`confirmar-${r.numero}`}><Pill tone="amber"><Hourglass className="w-3 h-3" />Por verificar · Confirmar</Pill></button>}
        <div className="text-[10px] mt-0.5 capitalize">{r.estado_entrega}</div>
      </td>
      <td className="px-2 py-2">
        {r.facturacion === "Facturado" ? <Pill tone="green">Facturado</Pill>
          : <Link to={`/facturacion?q=${r.numero}`} data-testid={`facturar-${r.numero}`}><Pill tone="purple"><FileText className="w-3 h-3" />Facturar{r.facturacion === "Parcial" ? " (parcial)" : ""}</Pill></Link>}
      </td>
    </tr>
  );
};

export const DiaReporte = ({ d, abierto, onChanged }) => {
  const [open, setOpen] = useState(abierto);
  const pend = d.n - d.confirmados;
  return (
    <div className="rounded-xl border bg-white overflow-hidden" data-testid={`dia-${d.fecha}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex flex-wrap items-center gap-2 px-3 py-2 text-left" data-testid={`dia-toggle-${d.fecha}`}>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <CalendarDays className="w-4 h-4 text-violet-700" /><b className="mr-auto">{fechaLarga(d.fecha)}</b>
        <Pill tone="zinc"><Truck className="w-3 h-3" />{d.n} Pedidos</Pill>
        {pend === 0 ? <Pill tone="green">Todo revisado</Pill> : <><Pill tone="green">{d.confirmados} Confirmados</Pill><Pill tone="amber">{pend} Por confirmar</Pill></>}
        <span className="text-sm">Total cobrado: <b><Q v={d.total} /></b></span>
      </button>
      {open && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs text-left">
              <tr>{["#", "Cliente / Pedido", "Entrega", "Productos (cant.)", "Anticipo / Forma(s) de pago", "Comprobante", "Estado / Confirmación", "Facturación"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr>
            </thead>
            <tbody>{d.pedidos.map((r, i) => <Fila key={r.id} r={r} i={i} onChanged={onChanged} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};
